import { Vector3 } from 'three';
import type { Sphere } from 'three';

const away = new Vector3();
const way = new Vector3();
const nearest = new Vector3();

/** A glove on its way in a punch, as the stretch from the wrist to the middle of the fist, as thick as the glove. */
export interface PunchingArm {
  readonly wrist: Vector3;
  readonly fist: Vector3;
  readonly radius: number;
}

/**
 * Keeps a glove, as a ball of this radius, out of a body made of spheres: each one it sinks into pushes it back out
 * to rest on the surface. A few passes settle it where two parts meet. True when it had to move.
 */
export function pushOut(centre: Vector3, radius: number, parts: readonly Sphere[], passes = 3): boolean {
  let moved = false;
  for (let pass = 0; pass < passes; pass++) {
    let clear = true;
    for (const part of parts) {
      const reach = part.radius + radius;
      away.subVectors(centre, part.center);
      const distance = away.length();
      if (distance >= reach) continue;
      // Dead centre has no way out: straight towards the viewer is as good as any.
      if (distance < 1e-6) away.set(0, 0, 1);
      centre.copy(part.center).addScaledVector(away.normalize(), reach);
      clear = false;
      moved = true;
    }
    if (clear) break;
  }
  return moved;
}

/** How far a glove sinks into the deepest part of a body, 0 when it touches nothing. */
export function deepest(centre: Vector3, radius: number, parts: readonly Sphere[]): number {
  let depth = 0;
  for (const part of parts) depth = Math.max(depth, part.radius + radius - centre.distanceTo(part.center));
  return depth;
}

/**
 * How far along its way from one frame to the next a glove, a ball of this radius, gets before it first touches a
 * part (0..1): 1 when nothing is in its way. One already resting on a part goes no further into it, but leaves freely.
 */
export function sweep(from: Vector3, to: Vector3, radius: number, parts: readonly Sphere[]): number {
  way.subVectors(to, from);
  const a = way.lengthSq();
  if (a < 1e-12) return 1;
  let first = 1;
  for (const part of parts) {
    // Where |from - centre + way·t| = reach: a·t² + 2b·t + c = 0.
    away.subVectors(from, part.center);
    const reach = part.radius + radius;
    const b = away.dot(way);
    const c = away.lengthSq() - reach * reach;
    if (c <= 1e-9) {
      if (b < 0) return 0;
      continue;
    }
    const d = b * b - a * c;
    if (b >= 0 || d < 0) continue;
    first = Math.min(first, (-b - Math.sqrt(d)) / a);
  }
  return first;
}

/**
 * Shoves a guard's glove, a ball of this radius, out of the way of punching arms: straight out sideways from each arm,
 * so it is moved the same way however far the fist has come, and never back, where there may be no room. A glove just
 * ahead of the fist makes way too. True when it had to move.
 */
export function pushOff(centre: Vector3, radius: number, arms: readonly PunchingArm[]): boolean {
  let moved = false;
  for (const arm of arms) {
    const reach = arm.radius + radius;
    way.subVectors(arm.fist, arm.wrist);
    const length = way.length();
    if (length < 1e-6) continue;
    way.divideScalar(length);
    const along = Math.max(0, away.subVectors(centre, arm.wrist).dot(way));
    if (along > length + reach) continue;
    nearest.copy(arm.wrist).addScaledVector(way, along);
    away.subVectors(centre, nearest);
    const distance = away.length();
    if (distance >= reach) continue;
    if (distance < 1e-6) away.set(0, 1, 0);
    centre.copy(nearest).addScaledVector(away.normalize(), reach);
    moved = true;
  }
  return moved;
}
